import React, { useEffect } from 'react'
import { NewRelic } from '@e3dc-react/shell/Types/globalTypes'
import { isTablet, isMobileOnly } from '@e3dc-react/shell/Libs/detect-device'
import { UserData } from 'src/State/User/UserModel'
import { useFeatureFlagsContext } from '../feature-flags/FeatureFlagsContext'
import { getWebviewPortalVersion } from '@e3dc-react/shell/Libs/webview'
import { newRelicAgentProduction, newRelicAgentStaging } from '.'
import { APP_VERSION_KEY } from 'src/Hooks/use-clear-cache-on-version-change'

interface ObservabilityAnalyticsProps {
  user?: UserData
}

const WEBVIEW_USER_AGENT_OBSERVABILITY_KEY = 'webview'
const WEBVIEW_USER_AGENT_TABLET_VALUE = 'tablet'
const WEBVIEW_USER_AGENT_MOBILE_VALUE = 'mobile'
const WEBVIEW_USER_AGENT_OTHER_VALUE = 'other'
const WEBVIEW_USER_AGENT_KEY = 'Portal'
const USERLEVEL_OBSERVABILITY_KEY = 'user-level'
const REACT_APP_VERSION_KEY = 'react_app_version'
const MOBILE_APP_VERSION_KEY = 'mobile_app_version'

/**
 * This component provides a script for frontend sided analytics.
 */

// Helper to inject New Relic script
const injectNewRelicScript = (newRelicId: string): void => {
  const script = document.createElement('script')
  const isProductionEnvironment = process.env.NODE_ENV === 'production'
  const newRelicAgentToUse = isProductionEnvironment ? newRelicAgentProduction : newRelicAgentStaging
  script.innerHTML = newRelicAgentToUse.replace(/(\r\n|\n|\r)/gm, '')
  script.id = 'newrelic-script'
  document.head.appendChild(script)
}

// Helper to get observability value
const getObservabilityValue = (): string => {
  if (isTablet) {
    return WEBVIEW_USER_AGENT_TABLET_VALUE
  } else if (isMobileOnly) {
    return WEBVIEW_USER_AGENT_MOBILE_VALUE
  }
  return WEBVIEW_USER_AGENT_OTHER_VALUE
}

// Helper to set custom attributes
const setCustomAttributes = (newRelic: NewRelic['newrelic'], user: UserData | undefined): void => {
  const reactAppVersion = localStorage.getItem(APP_VERSION_KEY)
  const mobileAppVersion = getWebviewPortalVersion()

  if (user) newRelic.setCustomAttribute(USERLEVEL_OBSERVABILITY_KEY, user.level)
  if (reactAppVersion) newRelic.setCustomAttribute(REACT_APP_VERSION_KEY, reactAppVersion)
  if (mobileAppVersion) newRelic.setCustomAttribute(MOBILE_APP_VERSION_KEY, mobileAppVersion)

  const observabilityValue = getObservabilityValue()
  if (navigator.userAgent.includes(WEBVIEW_USER_AGENT_KEY)) {
    newRelic.setCustomAttribute(WEBVIEW_USER_AGENT_OBSERVABILITY_KEY, observabilityValue)
  }
}

const ObservabilityAnalytics: React.FC<ObservabilityAnalyticsProps> = ({ user }) => {
  const {
    featureFlags: { isObservabilityAnalyticsEnabled },
    isFeatureFlagsLoading,
  } = useFeatureFlagsContext()
  const newRelicId = process.env.REACT_APP_NEWRELIC_ID
  const isDevelopmentEnvironment = process.env.NODE_ENV === 'development'

  useEffect(() => {
    if (
      !isDevelopmentEnvironment &&
      isObservabilityAnalyticsEnabled &&
      !isFeatureFlagsLoading &&
      document.getElementById('newrelic-script') === null &&
      newRelicId
    ) {
      injectNewRelicScript(newRelicId)
    }

    const newRelic = (window as unknown as NewRelic).newrelic
    if (newRelic) {
      setCustomAttributes(newRelic, user)
    }
  }, [isObservabilityAnalyticsEnabled, isFeatureFlagsLoading, newRelicId, user, isDevelopmentEnvironment])

  return <></>
}

export default ObservabilityAnalytics
